import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Company, Profile } from 'app/models';
import { CompanyService, SnackBarService, UsersService } from 'app/services';
import { catchError, of, Subject, takeUntil, zip } from 'rxjs';
import { Location } from '@angular/common';
import * as tableHelpers from 'app/helpers/tableSelectionHelpers';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarType } from 'app/constants/Enums';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { UserSelectionDialogComponent } from '../../dialogs/user-selection-dialog/user-selection-dialog.component';
import { ClosedBranchDialogComponent } from '../../dialogs/closed-branch-dialog/closed-branch-dialog.component';

@Component({
  standalone: false,
  selector: 'app-branch-admins',
  templateUrl: './branch-admins.component.html',
  styleUrls: ['./branch-admins.component.scss']
})
export class BranchAdminsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private readonly onDestroy$ = new Subject<void>();
  selection = new SelectionModel<Profile>(true, []);
  usersDataSource = new MatTableDataSource<Profile>([]);
  companyInitials: string;
  company: Company;
  noRecordFound = false;
  branchInfo: Company = null;
  branchHeader: string = null;
  branchUsers: any[] = [];
  branchAdmins: any[] = [];
  message = 'No_Users_Found';
  accountId: string = null;
  displayedColumns = ['select', 'name', 'email'];
  currentPageUsers: Profile[];
  isLoading: boolean = false;

  constructor(
    private companyService: CompanyService,
    private readonly usersService: UsersService,
    public readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private dialog: MatDialog,
    private readonly snackbarService: SnackBarService,
    private readonly translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.accountId = this.route.snapshot.params['branchId'];
    this.setCompanyDetails(this.accountId);
    this.updateCurrentPage();
  }

  updateCurrentPage(){
    this.usersDataSource
      .connect()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((users) => {
        this.currentPageUsers = users;
      });
  }

  setCompanyDetails(branchId?: string): void {
    this.companyService.company$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(company => {
        if (company) {
          this.initializeCompanyDetails(company, branchId);
        } else {
          this.navigateToCompanyOverview();
        }
      });
  }

  private initializeCompanyDetails(company: Company, branchId: string): void {
    this.company = company;
    this.branchInfo = this.getBranchDetails(branchId);
    if(this.isEqual(this.branchInfo.status, "Inactive")) {
      this.openClosedBranchInfoDialog();
    }
    this.parseName();
    this.setBranchHeader(this.branchInfo);
    this.setBranchUsers();
    this.updateUsersToDisplay();
  }

  private openClosedBranchInfoDialog(){
    this.dialog.open(ClosedBranchDialogComponent, {
      width: '600px',
      data :{content : 'COMPANY_BRANCHES.closedBranchAdminDialog'}
    });

  }

  private navigateToCompanyOverview(): void {
    this.router.navigate(['/company', this.route.snapshot.params['companyId']], { queryParams: { index: 1 } });
  }

  setBranchHeader(branch: Company): void {
    this.branchHeader = `${branch.name} ${branch.city}, ${branch.state}`;
    if(this.isEqual(branch.status, 'Inactive')){
      this.translateService.get('COMPANY_BRANCHES.closedHeadingTitle').pipe(takeUntil(this.onDestroy$))
      .subscribe(data=>this.branchHeader+=` (${data})`);      
    }
  }

  getBranchDetails(branchId: string): Company {
    return this.company.branches.find(branch => branch.accountId === branchId);
  }

  updateUsersToDisplay(): void {
    if (this.branchAdmins.length === 0) {
      this.noRecordFound = true;
    } else {
      this.setupTable(this.branchAdmins);
    }
  }

  setupTable(users: Profile[] = []): void {
    if (users.length === 0) {
      this.noRecordFound = true;
      return;
    }
    this.noRecordFound = false;
    this.usersDataSource = new MatTableDataSource(users);
    setTimeout(()=>{
      this.usersDataSource.paginator = this.paginator;
      this.usersDataSource.sort = this.sort;
      this.updateCurrentPage();
    },20);
    
  }

  setBranchUsers(): void {
    zip([this.companyService.branchesUsers$, this.usersService.companyUsers$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([branches, users]) => {
        this.processBranchAndUsers(branches, users);
      });
  }

  private processBranchAndUsers(branches: any[], users: any[]): void {
    if(!branches || !users) return;
    const currentBranchUsers = branches.find(branch => branch.branchId === this.accountId)?.users || [];
    const nonAdminUsers = users.filter(user => !user.isCompanyAdmin);
    const authIds = currentBranchUsers.map(user => user.userId);
    this.branchUsers = nonAdminUsers.filter(user => authIds.includes(user.id));
    const branchAdminsAuth0Ids = this.branchInfo.branchAdmins;
    this.branchAdmins = this.branchUsers.filter(user => branchAdminsAuth0Ids.includes(user.id));
  }

  private parseName(): void {
    if (this.company.name) {
      this.companyInitials = `${this.company.name[0].toUpperCase()}${this.company.name[this.company.name.length - 1].toUpperCase()}`;
    }
  }

  isAllSelected = () => tableHelpers.isAllSelected(this.currentPageUsers, this.selection.selected);

  masterToggle = () => tableHelpers.masterToggle(this.currentPageUsers, this.selection);

  isPartiallySelected = () => tableHelpers.isPartiallySelected(this.currentPageUsers, this.selection.selected);


  addBranchAdmins(): void {
    const branchAdminsAuth0Ids = this.branchInfo.branchAdmins;
    const nonAdminsUsers: Profile[] = this.branchUsers.filter(user => !branchAdminsAuth0Ids.includes(user.id));

    if (nonAdminsUsers.length !== 0) {
      let title = '';
      let primaryButtonText = '';
      let secondaryButtonText = '';

      let titleObservable = this.translateService.get('COMPANY_DETAILS_HEADER.add_users');
      let primaryButtonObservable = this.translateService.get('COMPANY_DETAILS_HEADER.add');
      let secondaryButtonTextObservable = this.translateService.get('COMPANY_DETAILS_HEADER.cancel');

      zip([titleObservable, primaryButtonObservable, secondaryButtonTextObservable]).pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
        title = value[0];
        primaryButtonText = value[1];
        secondaryButtonText = value[2];
      });

      let dialogRef = this.dialog.open(UserSelectionDialogComponent, {
        disableClose: true,
        autoFocus: false,
        ariaLabel: title,
        ariaModal: false,
        data: {
          title: title,
          users: nonAdminsUsers,
          secondaryButtonText: secondaryButtonText,
          primaryButtonText: primaryButtonText,
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.addBranchAdminsInBranch(result);
        }
      });
    } else {
      let addUsersMessage
      let info;
      this.translateService
        .get('BRANCH_USERS_DIALOG.all_users_assigned')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => (addUsersMessage = value));
      this.translateService
        .get('SNACKBAR.info')
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((value) => (info = value));
      this.snackbarService.showSnackbar(SnackbarType.info, info, addUsersMessage);
    }
  }

  removeBranchAdmins() {
    if (this.selection.hasValue()) {
      let title = '';
      let content = '';
      let primaryButtonText = '';
      let secondaryButtonText = '';

      let titleObservable = this.translateService.get('BRANCH_USERS_DIALOG.remove_branch_admins');
      let contentObservable = this.translateService.get('BRANCH_USERS_DIALOG.remove_branch_admins_info');
      let primaryButtonObservable = this.translateService.get('Remove');
      let secondaryButtonTextObservable = this.translateService.get('MAKE_ADMIN_DIALOG.cancel');

      zip([titleObservable, contentObservable, primaryButtonObservable, secondaryButtonTextObservable]).pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
        title = value[0];
        content = value[1];
        primaryButtonText = value[2];
        secondaryButtonText = value[3];
      });
      let dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        disableClose: true,
        autoFocus: false,
        ariaLabel: title,
        ariaModal: false,
        data: {
          title: title,
          content: content,
          secondaryButtonText: secondaryButtonText,
          primaryButtonText: primaryButtonText,
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.removeBranchAdminsFromBranch();
        }
      });
    }
  }

  removeBranchAdminsFromBranch() {
    this.isLoading = true;
    this.usersService.removeBranchAdmins(this.branchInfo.accountId, this.selection.selected.map(x => x.id))
      .pipe(takeUntil(this.onDestroy$),
        catchError(() => this.handleError()))
      .subscribe(value => {
        if (value) {
          this.isLoading = false;
          this.showSnackbar(SnackbarType.success, 'SNACKBAR.success', 'SNACKBAR.remove_branch_admins');
        }
      });

    this.branchInfo.branchAdmins = this.branchInfo.branchAdmins.filter(authID => !this.selection.selected.find(user => user.id === authID));
    this.branchAdmins = this.branchUsers.filter(user => this.branchInfo.branchAdmins.includes(user.id));
    this.selection.clear();
    this.setupTable(this.branchAdmins);
  }

  addBranchAdminsInBranch(users: Profile[]) {
    this.isLoading = true;
    this.usersService.addBranchAdmins(this.branchInfo.accountId, users.map(x => x.id))
      .pipe(takeUntil(this.onDestroy$),
        catchError(() => this.handleError()))
      .subscribe(value => {
        if (value) {
          this.isLoading = false;
          this.branchInfo.branchAdmins.push(...users.map(x => x.id));
          this.branchAdmins.push(...users);
          this.setupTable(this.branchAdmins);
          this.showSnackbar(SnackbarType.success, 'SNACKBAR.success', 'SNACKBAR.add_branch_admins');
        }
      });
  }

  handleError(){
    this.isLoading = false;
    this.showSnackbar(SnackbarType.error, 'SNACKBAR.error', 'SNACKBAR.generic');
    return of();
  }

  showSnackbar(type: SnackbarType, infoTranslateString: string, messageTranslateString: string) {
    let info = '';
    let message = '';
    let infoObservable = this.translateService.get(infoTranslateString);
    let messageObservable = this.translateService.get(messageTranslateString);

    zip([infoObservable, messageObservable]).pipe(takeUntil(this.onDestroy$)).subscribe((value) => {
      info = value[0];
      message = value[1];
    });
    this.snackbarService.showSnackbar(type, info, message);
  }
  
  isEqual(value1 : string, value2: string) : boolean{
    if(!value1 || !value2) return false;
    return value1.toLocaleLowerCase() === value2.toLocaleLowerCase();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

}
